import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { fmtMoney } from '../../../common/utils/money';
import { formatDateOnly } from '../../../common/utils/date';
import { useClient } from '../../clients/contexts/clients';
import { useGeneratePdf } from '../../reports/contexts/reports';
import { downloadPdf } from '../../reports/utils/shell';
import { useBatches, useBatch } from '../contexts/liquidations';
import { useAccountStatement } from '../contexts/accounts';
import { pivotBatchByClient } from '../utils/liquidation-pivot';
import { buildClientBillReport } from '../utils/bill-report';

import '../styles/billing.css';

// Liquidación mensual de UN cliente: el detalle del batch del mes (líneas cuyo reparto
// tiene a este cliente como dueño, por vaca) + su estado de cuenta (saldo a la fecha,
// colores intuitivos: verde a favor / rojo a pagar) + los movimientos del mes. El
// documento PDF (buildClientBillReport) reúne todo con el mismo mecanismo HTML→PDF.
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const SOURCE_LABEL = { liquidation: 'Pensión', sale: 'Venta', exchange: 'Ajuste', liquidation_adjustment: 'Ajuste', payment: 'Pago', credit_note: 'Nota crédito', reversal: 'Reversa' };

export default function ClientLiquidation({ clientId }) {
    const [year, setYear] = useState(() => new Date().getFullYear());
    const [month, setMonth] = useState(() => new Date().getMonth() + 1);
    const years = useMemo(() => { const y = new Date().getFullYear(); return [y + 1, y, y - 1, y - 2]; }, []);

    const { data: client } = useClient(clientId);
    const { data: batchesData, isFetching: bf } = useBatches(useMemo(() => ({ year, month }), [year, month]));
    const batch = batchesData?.items?.[0];
    const { data: detail, isLoading, isFetching } = useBatch(batch?.id);

    // Estado de cuenta (ledger, doble-partida). Dos vistas: acumulada (saldo a la fecha =
    // "cuánto debe") y acotada al mes (movimientos + neto por concepto del período).
    const allTimeRange = useMemo(() => ({}), []);
    const monthRange = useMemo(() => {
        const mm = String(month).padStart(2, '0');
        const last = new Date(year, month, 0).getDate();
        return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(last).padStart(2, '0')}` };
    }, [year, month]);

    const { data: acctAll } = useAccountStatement(clientId, allTimeRange);
    const { data: acctMonth } = useAccountStatement(clientId, monthRange);

    const totalAcct = acctAll?.totals || {};
    const outstanding = Number(totalAcct.balance) || 0;
    const monthMovements = useMemo(() => acctMonth?.activity || [], [acctMonth]);
    const monthTotals = acctMonth?.totals || {};
    const monthBySource = acctMonth?.by_source || {};

    // Pivote del batch por dueño (helper puro) filtrado a este cliente. Mismos montos del
    // backend, sin recalcular (0001); compartido con la vista "Por cliente" del batch.
    const cows = useMemo(() => {
        const group = pivotBatchByClient(detail?.liquidations).find((g) => !g.is_planta && String(g.client?.id) === String(clientId));
        return group?.cows ?? [];
    }, [detail, clientId]);
    const grand = cows.reduce((a, c) => a + c.subtotal, 0);

    const balMod = outstanding > 0 ? 'debt' : outstanding < 0 ? 'credit' : '';
    const balLabel = outstanding > 0 ? 'Saldo a pagar' : outstanding < 0 ? 'Saldo a favor' : 'Cuenta al día';
    const hasContent = cows.length > 0 || monthMovements.length > 0 || outstanding !== 0;

    const { mutateAsync: generatePdf } = useGeneratePdf();
    const [pdfBusy, setPdfBusy] = useState(false);
    const onPdf = async () => {
        if (pdfBusy || !hasContent) return;
        setPdfBusy(true);
        try {
            const account = {
                total: { debit: totalAcct.debit, credit: totalAcct.credit, balance: totalAcct.balance },
                month: { debit: monthTotals.debit, credit: monthTotals.credit, movements: monthMovements, bySource: monthBySource },
            };
            const { html, filename, options } = buildClientBillReport({ client, year, month, cows, grand, account });
            const blob = await generatePdf({ html, filename, options });
            downloadPdf(blob, filename);
            toast.success('Liquidación descargada');
        } catch (err) {
            if (err?.status !== 403) toast.error(err?.message || 'No se pudo generar el PDF');
        } finally {
            setPdfBusy(false);
        }
    };

    const busy = bf || isFetching;

    return (
        <div className="billing-liq">
            <header className="billing-liq__header">
                <div>
                    <Link to="/admin/clients" className="billing-liq__back">Clientes</Link>
                    <h1 className="billing-liq__title">Liquidación · {client?.name || `#${clientId}`}</h1>
                </div>
                <div className="billing-liq__period">
                    <select value={month} onChange={(e) => setMonth(Number(e.target.value))} disabled={busy}>
                        {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                    <select value={year} onChange={(e) => setYear(Number(e.target.value))} disabled={busy}>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </header>

            <div className="billing-liq__bill-head">
                <div className="billing-liq__total-card">
                    <span className="billing-liq__total-label">Pensión del mes</span>
                    <span className="billing-liq__total-value">{fmtMoney(grand)}</span>
                </div>
                <div className={`billing-liq__total-card ${balMod ? `billing-liq__total-card--${balMod}` : ''}`}>
                    <span className="billing-liq__total-label">{balLabel}</span>
                    <span className="billing-liq__total-value">{fmtMoney(Math.abs(outstanding))}</span>
                    <span className="billing-liq__total-break">Cargos {fmtMoney(totalAcct.debit)} · Abonos {fmtMoney(totalAcct.credit)}</span>
                </div>
                <Gate domain="reports" action="generate">
                    <button type="button" className="billing-btn billing-btn--primary" onClick={onPdf} disabled={pdfBusy || !hasContent}>
                        {pdfBusy ? 'Generando…' : 'Descargar PDF'}
                    </button>
                </Gate>
            </div>

            <section className="billing-liq__section">
                <h2 className="billing-liq__section-title">Detalle de la liquidación · {MONTHS[month - 1]} {year}</h2>
                {isLoading && batch ? (
                    <p className="billing-liq__hint">Cargando liquidación…</p>
                ) : !batch ? (
                    <p className="billing-liq__hint">No hay liquidación generada para {MONTHS[month - 1]} {year}. Generala en Facturación → Liquidaciones.</p>
                ) : !cows.length ? (
                    <p className="billing-liq__hint">Este cliente no tiene cargos de pensión en la liquidación de {MONTHS[month - 1]} {year}.</p>
                ) : (
                    <div className="billing-liq__cows">
                        {cows.map((c) => (
                            <article key={c.animal?.id} className="billing-liq__cow">
                                <header className="billing-liq__cow-head">
                                    <span className="billing-liq__cow-name">{c.animal?.name || c.animal?.number || '—'}</span>
                                    <span className="billing-liq__cow-meta">{[c.animal?.number, c.percent != null ? `tu ${c.percent}%` : null].filter(Boolean).join(' · ')}</span>
                                    <span className="billing-liq__cow-subtotal">{fmtMoney(c.subtotal)}</span>
                                </header>
                                <ul className="billing-liq__lines">
                                    {c.lines.map((l, i) => (
                                        <li key={i} className="billing-liq__line">
                                            <span>Manejo {l.management || '—'} · {l.days ?? '—'} días{l.day_rate != null ? ` · ${fmtMoney(l.day_rate)}/día` : ''}</span>
                                            <span>{fmtMoney(l.amount)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </article>
                        ))}
                    </div>
                )}
            </section>

            <section className="billing-liq__section">
                <h2 className="billing-liq__section-title">Movimientos del mes</h2>
                {!monthMovements.length ? (
                    <p className="billing-liq__hint">Sin movimientos registrados en la cuenta para {MONTHS[month - 1]} {year}.</p>
                ) : (
                    <div className="cat-overview__card">
                        <table className="cat-table cat-table--compact">
                            <thead><tr><th>Fecha</th><th>Concepto</th><th>Origen</th><th>Cargo</th><th>Abono</th></tr></thead>
                            <tbody>
                                {monthMovements.map((m) => (
                                    <tr key={m.entry_id} className="cat-table__row">
                                        <td>{formatDateOnly(m.entry_date) || '—'}</td>
                                        <td style={{ textAlign: 'left' }}>{m.description || '—'}</td>
                                        <td>{SOURCE_LABEL[m.source_type] || m.source_type}</td>
                                        <td>{m.direction === 'debit' ? fmtMoney(m.amount) : ''}</td>
                                        <td>{m.direction === 'credit' ? fmtMoney(m.amount) : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
